import HiddenInput from "@/components/form/components/HiddenInput";
import { selectStandardProps } from "@/utils/componentUtils";
import { licensePlateNormalizeFunction, upperCaseStringNormalizeFunction } from "@/utils/formUtils";
import { removeStringWhiteSpaces } from "@/utils/utils";
import { validationFunctions, validations } from "@/utils/validationUtils";
import { App as AntApp, AutoComplete, Col, Form, Row, Select, Spin } from "antd";
import { DataSourceItemObject } from "antd/es/auto-complete/AutoComplete";
import debounce from "lodash/debounce";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGetVehiclesAutocomplete } from "../../../../../../../common/api/queries";
import { rowGutter } from "../../../../../../../common/constants";
import { Client } from "../../../../../../client/types";
import { formatClientName } from "../../../../../../client/utils";
import { showVehicleFoundModal } from "../../../../../../vehicle/autocomplete/components/VehicleFoundModal";
import { AutocompleteRequestOrigin, AutocompleteVehiclesBy } from "../../../../../../vehicle/autocomplete/enums";
import { CreateUpdateInsuranceContract, CreateUpdateVehicleInsurance, Vehicle } from "../../../../../types";
import { parseVehicleModelName } from "../../../../../utils";

const AUTOCOMPLETE_MAX_SIZE = 7;
const colSpan = 6;

interface Props {
  index: number;
  clients: (Partial<Client> | undefined)[];
  vehicle?: Vehicle;
}

export const OtherVehicleInsuranceFormPart = ({ index, clients, vehicle }: Props) => {
  const { t } = useTranslation();
  const { modal } = AntApp.useApp();

  const [autocompleteKeyword, setAutocompleteKeyword] = useState<string>();
  const [autocompleteVehicleBy, setAutocompleteVehicleBy] = useState<AutocompleteVehiclesBy>(
    AutocompleteVehiclesBy.LICENSE_PLATE,
  );
  const [loadedVehicleName, setLoadedVehicleName] = useState<string>(() =>
    vehicle
      ? parseVehicleModelName({
          customBrand: vehicle.customBrand,
          brandName: vehicle.model.brand.name,
          customModel: vehicle.customModel,
          modelName: vehicle.model.name,
        })
      : "",
  );

  const { data: vehicleAutocompleteResult, isFetching } = useGetVehiclesAutocomplete({
    keyword: removeStringWhiteSpaces(autocompleteKeyword?.toUpperCase()),
    by: autocompleteVehicleBy,
    maxResultSize: AUTOCOMPLETE_MAX_SIZE,
    origin: AutocompleteRequestOrigin.GENERIC_CONTRACT,
  });

  const form = Form.useFormInstance<CreateUpdateInsuranceContract>();
  const formNamePrefix = ["insurances", index];

  const handleAutocompleteSearchDebounced = useMemo(
    () =>
      debounce((value: string, focusedItem: AutocompleteVehiclesBy) => {
        const keyword = removeStringWhiteSpaces(value);

        if (validationFunctions.validateSearchKeyword(keyword)) {
          setAutocompleteKeyword(keyword);
          setAutocompleteVehicleBy(focusedItem);
        }
      }, 500),
    [],
  );

  const handleAutocompleteSearch = useCallback(
    (value: string, focusedItem: AutocompleteVehiclesBy) => {
      handleAutocompleteSearchDebounced(value, focusedItem);
    },
    [handleAutocompleteSearchDebounced],
  );

  const getInsurances = (): CreateUpdateVehicleInsurance[] => {
    return [...(form.getFieldValue(["insurances"]) || [])];
  };

  const resolveAutocompleteOptions = (focusedItem: AutocompleteVehiclesBy): DataSourceItemObject[] => {
    return focusedItem === vehicleAutocompleteResult?.by
      ? vehicleAutocompleteResult?.data.map<DataSourceItemObject>((vehicle) => ({
          value:
            focusedItem === AutocompleteVehiclesBy.VIN
              ? vehicle.vin
              : (licensePlateNormalizeFunction(vehicle.licensePlate) ?? ""),
          text:
            focusedItem === AutocompleteVehiclesBy.VIN
              ? vehicle.vin
              : (licensePlateNormalizeFunction(vehicle.licensePlate) ?? ""),
        }))
      : [];
  };

  const handleAutocompleteSelect = (value: string, focusedItem: AutocompleteVehiclesBy) => {
    setVehicleData(
      vehicleAutocompleteResult?.data.find((vehicle) =>
        focusedItem === AutocompleteVehiclesBy.VIN
          ? vehicle.vin === value
          : vehicle.licensePlate === removeStringWhiteSpaces(value),
      ) as Vehicle,
      focusedItem,
    );
  };

  const setVehicleData = (vehicle: Vehicle, focusedItem?: AutocompleteVehiclesBy) => {
    const insurances = getInsurances();
    const licensePlate = insurances[index]?.licensePlate;

    showVehicleFoundModal({
      modal,
      vehicle,
      licensePlate,
      focusedItem,
      onConfirm: () => {
        insurances[index] = {
          ...(insurances[index] as CreateUpdateVehicleInsurance),
          licensePlate:
            focusedItem === AutocompleteVehiclesBy.VIN && !!licensePlate
              ? licensePlate
              : licensePlateNormalizeFunction(vehicle.licensePlate),
          vin: vehicle.vin,
          vehicleId: vehicle.id,
        };

        setLoadedVehicleName(
          parseVehicleModelName({
            customBrand: vehicle.customBrand,
            brandName: vehicle.model.brand.name,
            customModel: vehicle.customModel,
            modelName: vehicle.model.name,
          }),
        );
        form.setFieldsValue({ insurances });
      },
    });
  };

  useEffect(() => {
    const vehicle = vehicleAutocompleteResult?.data[0];
    const vehicleAutocompleteBy = vehicleAutocompleteResult?.by;

    const isExactSingleMatch =
      vehicleAutocompleteResult?.data.length === 1 &&
      vehicleAutocompleteResult?.keyword ===
        (vehicleAutocompleteBy === AutocompleteVehiclesBy.VIN ? vehicle?.vin : vehicle?.licensePlate);

    if (!isExactSingleMatch || !vehicle) {
      return;
    }

    setVehicleData(vehicle, vehicleAutocompleteBy);
  }, [vehicleAutocompleteResult]);

  // The API rejects a vehicleId whose vehicle carries a different vin, so the link is dropped once the vin is edited
  const handleVinChange = () => {
    const insurances = getInsurances();

    if (insurances[index]?.vehicleId) {
      insurances[index] = { ...(insurances[index] as CreateUpdateVehicleInsurance), vehicleId: undefined };
      setLoadedVehicleName("");
      form.setFieldsValue({ insurances });
    }
  };

  return (
    <>
      <HiddenInput name={[...formNamePrefix, "vehicleId"]} />

      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Spin spinning={isFetching && autocompleteVehicleBy === AutocompleteVehiclesBy.LICENSE_PLATE}>
            <Form.Item
              name={[...formNamePrefix, "licensePlate"]}
              label={t("contract.attrs.insurances.licensePlate")}
              rules={[validations.licensePlate, validations.noRepeatedInsuranceLicensePlate(index)]}
              normalize={licensePlateNormalizeFunction}
            >
              <AutoComplete
                popupMatchSelectWidth={false}
                showSearch={{
                  onSearch: (value) => handleAutocompleteSearch(value, AutocompleteVehiclesBy.LICENSE_PLATE),
                }}
                onSelect={(value: string) => handleAutocompleteSelect(value, AutocompleteVehiclesBy.LICENSE_PLATE)}
                options={resolveAutocompleteOptions(AutocompleteVehiclesBy.LICENSE_PLATE)}
              />
            </Form.Item>
          </Spin>
        </Col>

        <Col span={colSpan}>
          <Spin spinning={isFetching && autocompleteVehicleBy === AutocompleteVehiclesBy.VIN}>
            <Form.Item
              name={[...formNamePrefix, "vin"]}
              label={t("contract.attrs.insurances.vin")}
              rules={[validations.notBlank, validations.size(3, 17), validations.noRepeatedInsuranceVin(index)]}
              normalize={upperCaseStringNormalizeFunction}
            >
              <AutoComplete
                popupMatchSelectWidth={false}
                showSearch={{ onSearch: (value) => handleAutocompleteSearch(value, AutocompleteVehiclesBy.VIN) }}
                onSelect={(value: string) => handleAutocompleteSelect(value, AutocompleteVehiclesBy.VIN)}
                onChange={handleVinChange}
                options={resolveAutocompleteOptions(AutocompleteVehiclesBy.VIN)}
              />
            </Form.Item>
          </Spin>
        </Col>
      </Row>

      {loadedVehicleName && (
        <div className="sub-header-info margin-bottom-small">
          <span>{t("contract.attrs.insurances.vehicle._label")}: </span>
          {loadedVehicleName}
        </div>
      )}

      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item
            name={[...formNamePrefix, "vehicleHolderIdentifier"]}
            label={t("contract.attrs.insurances.vehicleHolderIdentifier")}
            rules={[validations.notNull]}
          >
            <Select
              {...selectStandardProps}
              options={clients
                .filter((client): client is Client => !!client)
                .map((c) => ({ value: c.identifier, label: formatClientName(c) }))}
            />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name={[...formNamePrefix, "vehicleOwnerIdentifier"]}
            label={t("contract.attrs.insurances.vehicleOwnerIdentifier")}
            rules={[validations.notNull]}
          >
            <Select
              {...selectStandardProps}
              options={clients
                .filter((client): client is Client => !!client)
                .map((c) => ({ value: c.identifier, label: formatClientName(c) }))}
            />
          </Form.Item>
        </Col>
      </Row>
    </>
  );
};
